import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useSpaces } from '../contexts/SpacesContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';

const toDate = (v) => {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  return new Date(v);
};

const AdminMemberBookingsPage = () => {
  const { memberId } = useParams();
  const { userProfile } = useAuth();
  const { spaces } = useSpaces();
  const [member, setMember] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    if (!memberId) return;
    const load = async () => {
      setLoading(true);
      try {
        const userSnap = await getDoc(doc(db, 'users', memberId));
        if (userSnap.exists()) setMember({ id: userSnap.id, ...userSnap.data() });

        const q = query(collection(db, 'bookings'), where('userId', '==', memberId));
        const snap = await getDocs(q);
        const all = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((b) => b.type !== 'guest')
          .sort((a, b) => {
            const aDate = toDate(a.startDate);
            const bDate = toDate(b.startDate);
            return (bDate || 0) - (aDate || 0);
          });
        setBookings(all);
      } catch (err) {
        console.error('Failed to load member bookings:', err);
      }
      setLoading(false);
    };
    load();
  }, [memberId]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-slate-500 py-20">Admin access required.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-400">
            <span className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
            Loading...
          </div>
        </div>
      </div>
    );
  }

  const displayName = member
    ? `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email
    : 'Unknown Member';

  const statusColor = (s) => s === 'confirmed' ? 'bg-green-100 text-green-700' : s === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700';
  const paymentColor = (s) => s === 'paid' ? 'bg-green-100 text-green-700' : s === 'failed' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700';

  const fmtBookingDate = (b) => {
    const start = toDate(b.startDate);
    const end = toDate(b.endDate);
    if (!start) return '—';
    if (b.pricingType === 'hourly' && end) {
      return `${format(start, 'd MMM yyyy, h:mm a')} – ${format(end, 'h:mm a')}`;
    }
    if (end) {
      const displayEnd = addDays(end, -1);
      return start.toDateString() === displayEnd.toDateString()
        ? format(start, 'd MMM yyyy')
        : `${format(start, 'd MMM yyyy')} → ${format(displayEnd, 'd MMM yyyy')}`;
    }
    return format(start, 'd MMM yyyy');
  };

  const active = bookings.filter((b) => b.status === 'confirmed');
  const cancelled = bookings.filter((b) => b.status === 'cancelled');
  const other = bookings.filter((b) => b.status !== 'confirmed' && b.status !== 'cancelled');

  const totalSpend = active.reduce((sum, b) => sum + (b.price || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 space-y-6">
        {/* Header */}
        <div>
          <Link to="/admin" className="text-sm text-brand-500 hover:underline mb-1 inline-block">← Back to Admin</Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-extrabold text-slate-800">{displayName}</h1>
              {member?.email && <p className="text-sm text-slate-400">{member.email}</p>}
              {member?.company && <p className="text-sm text-slate-400">{member.company}</p>}
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Total Spend</p>
              <p className="text-2xl font-bold text-slate-800">${totalSpend.toFixed(2)}</p>
              <p className="text-xs text-slate-400">{bookings.length} booking{bookings.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>

        {/* Member details */}
        {member && (
          <div className="card p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Phone</p>
                <p className="text-slate-700">{member.phone || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Role</p>
                <p className="text-slate-700">{member.role || 'member'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">Joined</p>
                <p className="text-slate-700">{member.createdAt ? format(toDate(member.createdAt), 'd MMM yyyy') : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wide">User ID</p>
                <p className="text-slate-700 font-mono text-xs">{memberId}</p>
              </div>
            </div>
          </div>
        )}

        {/* Bookings table */}
        {bookings.length === 0 ? (
          <div className="card p-8 text-center text-sm text-slate-400">No bookings found for this member.</div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden sm:block card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left">
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Space</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Dates</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Type</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400 text-right">Amount</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Payment</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {bookings.map((b) => {
                    const space = (spaces || []).find((s) => s.id === b.spaceId);
                    return (
                      <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3 font-medium" style={{ color: space?.color }}>{space?.name || b.spaceId}</td>
                        <td className="px-5 py-3 text-slate-500">{fmtBookingDate(b)}</td>
                        <td className="px-5 py-3 text-slate-500">{b.pricingType || '—'}</td>
                        <td className="px-5 py-3 text-slate-700 font-medium text-right">${(b.price || 0).toFixed(2)}</td>
                        <td className="px-5 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${paymentColor(b.paymentStatus)}`}>
                            {b.paymentStatus || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(b.status)}`}>
                            {b.status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <Link to={`/admin/booking/${b.id}`} className="text-brand-500 hover:underline text-xs font-medium">View</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="sm:hidden space-y-3">
              {bookings.map((b) => {
                const space = (spaces || []).find((s) => s.id === b.spaceId);
                return (
                  <Link key={b.id} to={`/admin/booking/${b.id}`} className="card p-4 block hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-1">
                      <span className="font-medium text-sm" style={{ color: space?.color }}>{space?.name || b.spaceId}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(b.status)}`}>{b.status}</span>
                    </div>
                    <p className="text-xs text-slate-500">{fmtBookingDate(b)}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${paymentColor(b.paymentStatus)}`}>{b.paymentStatus || '—'}</span>
                      <span className="text-sm font-bold text-slate-700">${(b.price || 0).toFixed(2)}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminMemberBookingsPage;
